import config from "./config.json";
import { plugins } from "./plugins";
import {
  Plugins,
  PluginBody,
  PluginName,
  PluginEnv,
  PluginConfig,
  NewDep
} from "./types";
import {
  getGlobal,
  CustomGlobalKeys
} from "@RHCommerceDev/utils-globals/getGlobal";
import merge from "lodash.merge";

const { requestIdleCallback } = window as any;

/**
 * Queues job in idle callback, falls back to setTimeout with timeout config
 * @param {Function} cb
 */
const ric = (cb: Function) =>
  config.useRic && requestIdleCallback
    ? requestIdleCallback(cb)
    : setTimeout(cb, config.fallbackRicTimeout);

/**
 * Shallow clones dependency array
 * @param {PluginConfig["dependencies"]} deps
 * @returns {Array<NewDep>}
 */
export const cloneDeps = <T extends PluginName>(
  deps: PluginConfig<T>["dependencies"]
): Array<NewDep<T>> => {
  return (deps as any).map((dep: any) => ({ ...dep, loaded: false }));
};

/**
 * Creates dependency elements from configuration, calls cb() once all are loaded
 * @param {NewDep} dep
 * @param {Array<NewDep>} deps
 * @param {Function} cb
 * @returns {Element}
 */
export const createDepElement = <T extends PluginName>(
  dep: NewDep<T>,
  deps: Array<NewDep<T>>,
  cb: Function
): Element => {
  const onload = () => {
    dep.loaded = true;
    if (deps.every(d => d.loaded)) {
      cb();
    }
  };
  const el = document.createElement(dep.tag ?? "script");
  Object.assign(el, dep.props, { onload });
  return el;
};

/**
 * Calls createDepElement & adds dependency elements to DOM
 * Adds plugin elements to DOM
 * @param {PluginBody} plugin
 * @param {PluginConfig} pluginConfig
 */
export const initPlugin = <T extends PluginName>(
  plugin: PluginBody<T>,
  pluginConfig: PluginConfig<T>
) => {
  const cb = () => plugin.init(pluginConfig);
  const deps = cloneDeps(pluginConfig.dependencies);
  deps
    .map(dep => createDepElement(dep as NewDep<T>, deps as NewDep<T>[], cb))
    .forEach(el => document.body.appendChild(el));
  pluginConfig.elements.forEach(el =>
    document.body.insertAdjacentHTML("beforeend", el.html)
  );
};

/**
 * Determines initBehavior and queues initPlugin()
 */
export const initWithBehavior = <T extends PluginName>(
  plugin: PluginBody<T>,
  pluginConfig: PluginConfig<T>
) => {
  if (pluginConfig.initBehavior === "idle") {
    ric(() => initPlugin(plugin, pluginConfig));
  } else {
    initPlugin(plugin, pluginConfig);
  }
};

export const isCookieOn = (cookie?: object): boolean => {
  if (!cookie) {
    return false;
  }
  const name = Object.keys(cookie)[0];
  return document.cookie.includes(`${name}=${(cookie as any)[name]}`);
};

export const isFeatureToggleOn = (name?: string): boolean => {
  if (!name) {
    return false;
  }
  return !!(name && getGlobal("_env")[name as CustomGlobalKeys]);
};

/*
 * Checks if plugin exists, feature toggle is true, appropriate cookie exists
 */
export const shouldTurnOn = <T extends PluginName>(
  plugin: PluginBody<T> | undefined,
  pluginConfig: PluginConfig<T>
): boolean =>
  !!(
    plugin &&
    (isCookieOn(pluginConfig.toggle?.cookie) ||
      isFeatureToggleOn(pluginConfig.toggle?.featureFlag))
  );

const getStagePlugins = (plugins: Plugins, stage: string) => {
  return (Object.keys(plugins) as [keyof Plugins]).reduce<Partial<Plugins>>(
    (stagePlugins, name) => {
      if (plugins[name].default.stage === stage) {
        stagePlugins[name] = plugins[name] as any;
      }
      return stagePlugins;
    },
    {}
  );
};

/**
 * Iterates through plugin configuration, and calls plugin.init(pluginConfig) if plugin should be turned on
 * @param {}
 */
export const init = (
  conf: Plugins = config.plugins,
  ps: { [key in PluginName]: PluginBody<key> } = plugins,
  stage: string = "init"
) => {
  Object.keys(getStagePlugins(conf, stage)).forEach(pk => {
    const c = conf[pk as PluginName];
    const pluginConfig = merge(
      {},
      c.default,
      c[getGlobal("_env").ENV_ID as PluginEnv]
    );
    const plugin = ps[pk as PluginName];
    if (shouldTurnOn(plugin, pluginConfig)) {
      initWithBehavior(plugin, pluginConfig);
    }
  });
};

// export const initSalesforcePlugin = (
//   environment: string,
//   conf: Plugins = config.plugins,
//   ps: { [key in PluginName]: PluginBody<key> } = plugins
// ) => {
//   const c = conf["SALESFORCE_CHAT" as PluginName];
//   const pluginConfig = merge(
//     { defer: true },
//     c.default,
//     c[environment as PluginEnv]
//   ) as PluginConfig<"SALESFORCE_CHAT">;

//   const plugin: PluginBody<"SALESFORCE_CHAT"> | undefined =
//     ps["SALESFORCE_CHAT"];
//   initWithBehavior(plugin, pluginConfig);
// };
